import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { listRedditSources, addSubreddit, deleteRedditSource, RedditSourceRow } from '../../services/onboarding';
import { ChannelIcon } from './channelIcons';

const input = 'flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500';
const ghost = 'inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 border border-gray-200 hover:bg-gray-50 px-3 py-2 rounded-lg disabled:opacity-50';

export default function RedditSourcesCard({
  suggested, onSuggest, suggesting,
}: { suggested: string[]; onSuggest: () => void; suggesting: boolean }) {
  const [rows, setRows] = useState<RedditSourceRow[]>([]);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = () => listRedditSources().then(setRows).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const have = new Set(rows.map(r => r.subreddit.toLowerCase()));

  const add = async (name: string) => {
    const clean = name.replace(/^\/?r\//i, '').trim();
    if (!clean || have.has(clean.toLowerCase())) return;
    setBusy(true);
    try { await addSubreddit(clean); await refresh(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || 'Could not add'); }
    finally { setBusy(false); }
  };

  const remove = async (id: string) => { await deleteRedditSource(id); refresh(); };

  return (
    <div className="border border-gray-100 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <ChannelIcon id="reddit" className="w-4 h-4" /> Reddit subreddits
          </h3>
          <p className="text-xs text-gray-500">Communities to monitor. Empty = Reddit is skipped.</p>
        </div>
        <button onClick={onSuggest} disabled={suggesting} className={ghost}>
          <SparklesIcon className="w-4 h-4" /> {suggesting ? 'Thinking…' : 'Suggest with AI'}
        </button>
      </div>

      {/* add input */}
      <div className="flex gap-2 mb-3">
        <input className={input} placeholder="e.g. dubai (without r/)" value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add(val), setVal(''))} />
        <button onClick={() => { add(val); setVal(''); }} disabled={busy || !val.trim()} className={ghost}>Add</button>
      </div>

      {/* AI suggestions to add */}
      {!!suggested.length && (
        <div className="mb-3">
          <p className="text-xs text-gray-500 mb-1.5">Suggested — click to add:</p>
          <div className="flex flex-wrap gap-1.5">
            {suggested.filter(s => !have.has(s.toLowerCase())).map(s => (
              <button key={s} onClick={() => add(s)}
                className="text-[11px] bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 px-2 py-0.5 rounded-full">
                + r/{s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* current list */}
      <div className="flex flex-wrap gap-1.5">
        {rows.map(r => (
          <span key={r.id} className="inline-flex items-center gap-1.5 text-xs font-medium bg-gray-50 border border-gray-200 text-gray-700 px-2.5 py-1 rounded-lg">
            r/{r.subreddit}
            <button onClick={() => remove(r.id)} className="text-gray-400 hover:text-red-500"><XMarkIcon className="w-3.5 h-3.5" /></button>
          </span>
        ))}
        {!rows.length && <p className="text-xs text-gray-400">No subreddits yet.</p>}
      </div>
    </div>
  );
}
